/** Values that are choices rather than measurements, kept in one place so they can
 *  be argued with instead of hunted for. */

/**
 * Fixed EUR per USD rate. Deliberately NOT a live feed: an estimate has to stay
 * explainable six months later, and a rate that moves under the reader's feet
 * would make two identical estimates disagree. The interface states the rate it
 * used, so a reader can redo the conversion.
 */
export const EUR_PER_USD = 0.92

/** Above this many draws, resampling stops paying for itself and the closed form
 *  takes over -- which is also the regime where it is most accurate. */
export const RESAMPLE_DRAW_LIMIT = 500

/** Above this measured dispersion, the archetype is so spread that the figure is
 *  an order of magnitude whatever the reading was worth. Measured range across the
 *  library: x1.2 on a repetitive cron to x9 on an open question. */
export const DISPERSION_ORDER_OF_MAGNITUDE = 6

export type ModelTier = 'leger' | 'standard' | 'avance'
export const TIER_ORDER: ModelTier[] = ['leger', 'standard', 'avance']

/* Capability scores used to live here. They now live in `capability.ts`, split by
 * domain: "which model is best" has no answer until you say at what, and one
 * generalist column was answering a question the reader had not asked. */

/**
 * How far below the best note a model may fall and still be offered as the best
 * value: 10 points out of 100.
 *
 * This is the only constant in the three recommendations, and it is a choice, so
 * the interface states it. It replaced two attempts that both failed for the same
 * reason — they ranked on price in disguise. "Highest note per euro" always
 * returns the cheapest model on the board, because dividing a note capped at 100
 * by a price spanning two orders of magnitude is just sorting by price. The knee
 * of the Pareto frontier on a log price axis did the same thing more subtly: it
 * offered +2 points for 1.5x the price and called it a compromise.
 *
 * Ten points is legible — a reader can decide for themselves whether ten points
 * of a benchmark matters for their job — and it is the number to move when a
 * benchmark of our own says the gap costs more than it looks.
 */
export const VALUE_GAP_POINTS = 10

/**
 * Le plafond de qualité, par type de travail : au-delà de cette note, sur CE travail, notre
 * bench ne mesure plus de gain, donc la carte « Le meilleur » s'y arrête et prend le moins cher
 * des modèles qui l'atteignent. Demande d'Armand (17/09/2026) : recommander le modèle le plus
 * cher pour une automatisation « n'a pas de sens, car ce serait overkill ».
 *
 * En POINTS AFFICHÉS (0..100) et dans l'unité DU DOMAINE de l'item : 38 sur l'Intelligence Index
 * n'est pas 38 sur le Coding Agent Index. Un plafond ne se recopie jamais d'un type de travail
 * vers un autre. Absent de la table = aucun plafond = comportement d'avant, à l'identique.
 *
 * 29, ce n'est pas un chiffre rond : c'est la note la plus basse qu'on ait vu réussir notre
 * bench d'automatisation à CHAQUE run — MiniMax M3, qui fait 97,28 puis 96,00 sur deux runs
 * complets. En dessous, le seul modèle testé rate ses deux runs : Claude Haiku 4.5, noté 17, à
 * 89,61 puis 92,59, et il manque les deux fois la même contradiction entre deux documents.
 * C'est donc un PLANCHER observé retourné en plafond, et la page le dit : il marque où la qualité
 * décroche, pas où elle cesse vraiment de monter.
 *
 * Ce nombre SUIT L'INDICE, il ne le fixe pas : MiniMax valait 30 au relevé du 11/09/2026 et 29 à
 * celui du 20/09, sans que rien ne change ni chez lui ni dans nos runs (data/index-history.json).
 * Le plafond a donc bougé d'un point, et aucune carte avec.
 *
 * Le plancher réel est plus bas : mesuré au réglage testé, GPT-5.6 Terra, que la grille note 42 à
 * son maximum, vaut 27 en low chez Artificial Analysis, et il y a réussi ses deux runs ; Ling 3.0
 * Flash, hors grille, rate le sien à 25, et GPT-5.6 Luna les deux siens à 21. À réglage égal, le
 * plancher est entre 25 et 27. Mais le plafond se compare aux notes AFFICHÉES, et aucun modèle de
 * la grille n'en affiche entre 17 et 29 : toute valeur de cet intervalle retiendrait exactement
 * les mêmes modèles.
 *
 * Le plafond se lit sur les modèles qui ont réussi CHAQUE run complet, jamais sur leur meilleur :
 * un même modèle au même réglage peut bouger de 9 points d'un run à l'autre, et déclarer tout un
 * champ équivalent au-dessus d'une note, sur un modèle qu'on a vu rater, serait une promesse
 * qu'on n'a pas mesurée. Grok 4.6 (noté 44) est ce cas — 86,11 puis 95,28 en low, 95 en medium
 * et en xhigh : un raté sur quatre, qu'un client rencontrerait une fois sur quatre — donc il
 * reste nommé parmi les modèles vus au travail, et il ne pose pas le plafond.
 *
 * Vérifié au moteur : de 29 à 39, aucune des quatre automatisations ne change de carte. La
 * valeur exacte ne déplace donc rien aujourd'hui ; elle dit ce qu'on affirme, pas ce qu'on
 * affiche. La marge a fondu, en revanche : à 40, DeepSeek v4.1 Flash — noté 40 le 11/09, 39 le
 * 20 — passerait sous le plafond, et la carte du haut irait à GLM-5.3-Flash sur trois
 * automatisations. Un point d'indice sépare donc désormais « rien ne bouge » de « trois cartes
 * changent » : raison de plus pour dater les relevés.
 *
 * Les projets restent sans plafond : le bench porte sur des automatisations, et rien ne dit
 * qu'un plafond tiendrait pour construire un logiciel. Sur le code, un point de note déplace la
 * carte du haut d'un facteur 67 sur le prix, pour une note dont `capability.ts` annonce
 * lui-même une dizaine de points d'incertitude.
 *
 * Recalculé depuis data/bench-automatisation.json par tests/cap.test.ts : changer la mesure,
 * pas ce chiffre.
 */
export const QUALITY_CAP_POINTS: Record<string, number | null> = {
  auto_short_task: 29,
  auto_report: 29,
  auto_synthesis: 29,
  auto_watch: 29,
  extension: null,
  site_vitrine: null,
  web_app: null,
  produit_complet: null,
}

/**
 * Le type de projet, et la tâche de notre bench qui lui ressemble.
 *
 * Nos quatre tâches de code SONT quatre types de projet : une app mobile, une application
 * web, une boutique, un site de contenu. Un type qui n'a pas la sienne garde la moyenne
 * des quatre — c'est le cas du produit complet (il les contient toutes), de l'API et de
 * l'extension (rien d'équivalent n'a été benché).
 */
export const BENCH_TASK_OF_TYPE: Record<string, string> = {
  app_mobile: 'instagram-mobile',
  web_app: 'ged-webapp',
  ecommerce: 'coffee-ecommerce',
  site_vitrine: 'french-a2',
}

/**
 * Ce que « faire documenter le travail » ajoute, et ce qu'il déplace.
 *
 * DÉCLARÉ (Armand, 21/09/2026). Un quart de jetons en plus, parce que la documentation
 * s'écrit ; et un quart de la note qui bascule sur l'indice généraliste, parce qu'écrire
 * n'est pas coder et que les modèles ne s'y classent pas pareil. Une seule valeur pour les
 * deux effets : ce qui pèse un quart du travail doit peser un quart du jugement.
 */
export const DOCUMENTATION_SHARE = 0.25

/**
 * Les types qu'on chiffre à partir d'un autre, et la part qu'on leur donne.
 *
 * DÉCLARÉES, comme HUMAN_DAYS : la mesure les encadre, elle ne les fixe pas. Ce qui est
 * mesuré (20/09/2026), c'est la part du travail attribuable à une sous-application dans les
 * trois monorepos du corpus — enishi, shush, kumo, qui sont tous des applications mobiles
 * avec leur API et un front web :
 *
 *  - en épisodes qui ne touchent QU'À elle : mobile 17 à 36 % du coût, API 3 à 5 %, web 0 à 5 % ;
 *  - en fichiers cités : mobile 45 à 55 %, API 10 à 32 %, web 8 à 9 %.
 *
 * Entre les deux, 59 à 74 % du travail est transverse — une même demande touche le mobile,
 * l'API et les paquets partagés — et ne se découpe pas sans clé de répartition. Les parts
 * ci-dessous SONT cette clé, et la page le dit.
 *
 *  - `app_mobile` 0,5 : la part du mobile seul — 45 à 55 % des fichiers cités, 17 à 36 % des
 *    épisodes dédiés. L'app SEULE : elle peut être sans serveur, ou parler à un service qui
 *    existe déjà. Celui qui a aussi son backend à construire ajoute l'API (Armand, 20/09/2026).
 *  - `api_backend` 0,25 : entre les 3-5 % d'épisodes dédiés et les 10-32 % de fichiers cités,
 *    plus la part du transverse qu'une API porte seule quand il n'y a pas d'app en face.
 *  - `ecommerce` 1,0 : une boutique est chiffrée comme une application web, faute d'en avoir
 *    construit une en vrai. Notre bench en a bâti une en un coup pour 8 $ au tarif de
 *    référence — c'est la première passe, pas le projet fini, et l'écart entre les deux est
 *    d'un facteur 12 à 22 sur les paires qu'on a mesurées.
 */
export const DERIVED_TYPES: Record<string, { base: string; share: number }> = {
  app_mobile: { base: 'produit_complet', share: 0.5 },
  api_backend: { base: 'produit_complet', share: 0.25 },
  ecommerce: { base: 'web_app', share: 1 },
}

/**
 * Ce que « petit », « moyen » et « large » valent, en part des fonctionnalités
 * du projet de référence — le plus gros de ce type qu'on ait réellement mesuré.
 *
 * C'est un CHOIX, pas une mesure, et c'est le seul endroit où le changer.
 *
 * Il repose sur une mesure, en revanche : sur les huit projets du corpus, le
 * coût suit la quantité de travail avec un exposant de 0,96 — quasiment
 * proportionnel. Un quart des fonctionnalités coûte donc à peu près un quart du
 * prix, pas un dixième ni la moitié. Un projet casse la règle d'un facteur 4
 * (kumo, fait en très peu de tours très lourds), donc c'est une tendance et pas
 * une constante.
 *
 * Le doublement est retenu parce que c'est la marche la plus lisible — « un
 * quart », « la moitié », « tout » — et parce que c'est ce qui colle le mieux aux
 * trois produits complets mesurés, à 16 %, 40 % et 100 % l'un de l'autre.
 */
export const TAILLE_PARTS = [0.25, 0.5, 1]

/** Coarse tiers, for the few places a band reads better than a number. Derived
 *  from the generalist index, so they are a rough sort and nothing more. */
export const MODEL_TIERS: Record<string, ModelTier> = {
  'anthropic/claude-fable-5.1': 'avance',
  'openai/gpt-6-astra': 'avance',
  'anthropic/claude-opus-5': 'avance',
  'anthropic/claude-opus-4.6': 'avance',
  'z-ai/glm-5.3': 'standard',
  'moonshotai/kimi-k3': 'standard',
  'z-ai/glm-5.3-flash': 'standard',
  'qwen/qwen3.8-max-0902': 'standard',
  'minimax/minimax-m3': 'leger',
  'x-ai/grok-4.6': 'standard',
  'openai/gpt-5.6-terra': 'standard',
  'openai/gpt-5.6-sol': 'standard',
  'deepseek/deepseek-v4.1-flash': 'standard',
  'anthropic/claude-sonnet-5': 'standard',
  'google/gemini-3.8-flash': 'leger',
  'openai/gpt-5.4-mini': 'leger',
  'anthropic/claude-haiku-4.5': 'leger',
}

/**
 * A rough number of person-days, proposed in the field rather than left blank.
 *
 * DECLARED, not measured, and the only figure in this product that is. Nothing in
 * the corpus measures how long a team takes: it measures what an agent consumed.
 * An empty field, though, asks a non-technical reader for the one number they are
 * least able to produce, and an estimate they cannot start is an estimate they
 * never finish.
 *
 * So the comparison opens already computed on this figure (Armand, 20/09/2026), and the
 * field says whose it is: « notre ordre de grandeur : remplacez-le par le vôtre ». It was a
 * placeholder until then, and a reader who had never commissioned this work left it empty,
 * which meant never seeing the comparison at all.
 *
 * Les deux ancres viennent d'Armand, qui construit ces projets (21/09/2026) : application
 * web 10/20/30, produit complet 15/30/45. Elles remplacent des chiffres deux à cinq fois
 * plus hauts, et elles disent quelque chose : l'échelle humaine est bien plus plate que
 * celle de l'agent (un produit complet coûte 2,4× une application web en jetons, mais
 * seulement 1,5× en jours). Les types dérivés suivent leur part (DERIVED_TYPES) ; la
 * vitrine et l'extension sont posées dans le même registre.
 *
 * Projects: [POC, MVP, projet complet] — la même échelle que les trois cartes de
 * l'étape 2. Automations: the person-days one run of the same task would take by hand.
 */
export const HUMAN_DAYS: Record<string, number[]> = {
  produit_complet: [15, 30, 45],
  web_app: [10, 20, 30],
  site_vitrine: [2, 4, 8],
  extension: [2, 4, 7],
  app_mobile: [8, 15, 23],
  api_backend: [4, 8, 12],
  ecommerce: [10, 20, 30],
  auto_short_task: [0.1],
  auto_report: [0.25],
  auto_synthesis: [0.5],
  auto_watch: [1],
}

/** Where to send someone who wants a real quote. This tool gives an order of
 *  magnitude on one developer's own projects; a number that binds anybody has to
 *  come from a conversation. */
export const CONTACT = {
  url: 'https://saru-lab.com',
  label_fr: 'Parler du projet',
  label_en: 'Talk about the project',
  pitch_fr: "Ce chiffre est un ordre de grandeur tiré de projets réels, pas un "
          + "devis : il ne contient ni le cadrage, ni vos allers-retours, ni la "
          + "reprise de l'existant. Pour une estimation plus fine et plus réaliste, "
          + "il est préférable de consulter un expert et d'analyser votre projet.",
  pitch_en: 'This figure is an order of magnitude drawn from real projects, not a quote: '
          + 'it holds neither the scoping, nor your back and forth, nor picking up an '
          + 'existing codebase. For a finer, more realistic estimate, it is better to ask '
          + 'an expert and look at your project.',
}

